import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Mail, ArrowLeft, ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 border-slate-200 shadow-sm fade-in">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#0066FF] flex items-center justify-center text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-2xl tracking-tight text-slate-900">FIO<span className="text-[#0066FF]">TELECOM</span></span>
        </div>

        {done ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <MailCheck className="h-6 w-6" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Vérifiez votre boîte e-mail</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              Si un compte est associé à cette adresse, vous recevrez sous peu un e-mail contenant un lien
              pour choisir un nouveau mot de passe. <b>Ce lien est valable 1 heure.</b>
            </p>
            <p className="text-xs text-slate-500 mt-4">Pensez à vérifier vos courriers indésirables.</p>
            <Button asChild variant="outline" className="mt-6 w-full"><Link to="/login">Retour à la connexion</Link></Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Mot de passe oublié</h2>
            <p className="text-sm text-slate-500 mt-2">
              Saisissez votre adresse e-mail : nous vous enverrons un lien sécurisé pour définir un nouveau mot de passe.
            </p>
            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Adresse e-mail professionnelle</Label>
                <div className="relative mt-1">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email" type="email" required autoComplete="email"
                    data-testid="forgot-email-input"
                    placeholder="prenom.nom@fiotelecom.fr"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 h-11"
                  />
                </div>
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#0066FF] hover:bg-[#0052CC]">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi…</> : "Envoyer le lien"}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-slate-800 mt-2">
                <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
